import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationManagedAdNetworkDto } from "../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { DIRECT_MAIL_VENDORS_SLUG } from "../surveys/sharingSurvey";
import { VENDOR_CATEGORY_SALES_MARKETING_TOOL } from "../types/Vendor";
import { parseMultiselectAnswerWithNoneOption } from "../surveys/survey";
import { getVendorNesting } from "./vendorNesting";
import { isGoogleAnalytics } from "./vendors";

// Exclude specifically Google Analytics from opt-out workflows (POL-7673)
export const isVendorAllowedInOptOutWorkflow = (vendor: OrganizationDataRecipientDto): boolean =>
  !isGoogleAnalytics(vendor);

// POL-7919: an upload flag can outlive the selling setting. Only the upload branch
// requires effective selling; custom audiences, direct mail, and managers remain independent.
export const isUploadVendorForOptOutWorkflow = (vendor: OrganizationDataRecipientDto): boolean =>
  vendor.isAlwaysUploadVendor === true ||
  (vendor.isUploadVendor === true &&
    (vendor.ccpaIsSelling === true || vendor.isAlwaysDataSellingVendor === true));

/** The recipient roles the Opt-Out workflow acts on: audience lists TrueVault uploads to, and direct mail. */
export const isOptOutWorkflowVendorType = (
  vendor: OrganizationDataRecipientDto,
  directMailVendorIds: string[],
): boolean =>
  isUploadVendorForOptOutWorkflow(vendor) ||
  Boolean(vendor.usesCustomAudience) ||
  directMailVendorIds.includes(vendor.id);

// Only consider Sales & Marketing vendors (per POL-6573)
export const isDirectMailEligibleVendor = (
  vendor: Pick<OrganizationDataRecipientDto, "category">,
): boolean => vendor.category === VENDOR_CATEGORY_SALES_MARKETING_TOOL;

type OptOutWorkflowVendorSelection = {
  directMailVendorIds: string[];
  managerVendorIds: string[];
  managedVendorIds: string[];
  hasManagedNetworks: boolean;
};

export const getOptOutWorkflowVendorSelection = (
  allVendors: OrganizationDataRecipientDto[],
  answers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
): OptOutWorkflowVendorSelection => {
  const nesting = getVendorNesting(allVendors, managedAdNetworks, answers, {
    isManagerEligible: isVendorAllowedInOptOutWorkflow,
  });

  const directMailVendorIds = parseMultiselectAnswerWithNoneOption(
    answers,
    DIRECT_MAIL_VENDORS_SLUG,
  );

  return {
    directMailVendorIds,
    managerVendorIds: nesting.activeManagerIds,
    managedVendorIds: nesting.managedVendorIds,
    hasManagedNetworks: nesting.managedVendorIds.length > 0,
  };
};

export const isVendorIncludedInOptOutWorkflow = (
  vendor: OrganizationDataRecipientDto,
  selection: OptOutWorkflowVendorSelection,
) => {
  if (!isVendorAllowedInOptOutWorkflow(vendor)) {
    return false;
  }

  const isManagedByAnotherVendor = selection.managedVendorIds.includes(vendor.id);
  const isIncludedByTypeLikeRemoveConsumer = isOptOutWorkflowVendorType(
    vendor,
    selection.directMailVendorIds,
  );

  const isSelectedManagerVendor =
    selection.hasManagedNetworks && selection.managerVendorIds.includes(vendor.id);

  return (
    (isIncludedByTypeLikeRemoveConsumer || isSelectedManagerVendor) && !isManagedByAnotherVendor
  );
};
